
import IncrediLink from '@/components/common/incrediLink.vue';
import IncrediSelectMenu from '@/components/inputs/incrediSelectMenu.vue';
import { PropType, defineComponent, inject } from 'vue';
import { IKSettingsDirty } from '@/types/injectionKeys';
import { QUERY_RESULT_PATH, SettingsPageWindowsQuery, useSettingsPageWindowsQuery } from '@/graphql/codegen/generated/graphql';
import { IKCoordinatorsService } from '@/modules/CoordinatorsService/CoordinatorService.InjectionKeys';
import SettingsItemBox from './settings-item-box.vue';

export default defineComponent({
  name: 'SettingsDropdownBox',
  components: {
    IncrediLink,
    SettingsItemBox,
    IncrediSelectMenu,
  },
  props: {
    disable: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: Object as PropType<NonNullable<SettingsPageWindowsQuery['ibManagerEntities']>>,
      required: true,
    },
  },
  emits: ['update:modelValue'],
  setup() {
    const settingsDirty = inject(IKSettingsDirty);
    const coordinatorsService = inject(IKCoordinatorsService);
    const GQL_SettingsCoordinatorVersion = useSettingsPageWindowsQuery(() => ({ coordinatorId: coordinatorsService?.selectedCoordinator.value || '' }));

    return {
      setInputsDirty: settingsDirty?.setInputsDirty,
      GQL_SettingsCoordinatorVersion,
    };
  },

  data() {
    return {
      learnMoreLink: this.$t('settings.summary.windows.general.backup.learnMore.url'),
      selectedBackupName: '',
      noConfiguredBackupOption: this.$t('settings.general.noConfiguredBackupOption'),
      shouldDisplayIncompatibleNote: false,
    };
  },
  computed: {
    backupMachines() {
      const { backupCoordinatorNames, backupCoordinatorHosts, backupCoordinatorPorts, backupCoordinatorBuildNumbers } = this.modelValue?.settings?.common?.backup || {};
      const machinesNames = [...(backupCoordinatorNames || '').split(',')];
      const machinesIps = [...(backupCoordinatorHosts || '').split(',')];
      const machinesPorts = [...(backupCoordinatorPorts || '').split(',')];
      const machinesBuildNums = [...(backupCoordinatorBuildNumbers || '').split(',')];
      const machinesObject: any = {};
      for (let i = 0; i < machinesNames.length; i++) {
        const item = this.getBackupObjectDefault();
        item.name = machinesNames[i];
        item.port = machinesPorts[i] ? parseInt(machinesPorts[i]) : item.port;
        item.ip = machinesIps[i] ? machinesIps[i] : item.ip;
        item.buildNum = machinesBuildNums[i] ? parseInt(machinesBuildNums[i]) : item.buildNum;
        machinesObject[machinesNames[i]] = item;
      }
      return machinesObject;
    },
    backupMachinesNamesList() {
      const { backupCoordinatorNames } = this.modelValue?.settings?.common?.backup || {};

      return [this.noConfiguredBackupOption, ...(backupCoordinatorNames || '').split(',')];
    },
  },
  mounted() {
    // set default selected option
    if (this.shouldDisplayBackupDropdown()) {
      const backupList = this.modelValue?.settings?.common?.backup?.otherCoordinatorName ? this.modelValue?.settings?.common?.backup?.otherCoordinatorName : this.noConfiguredBackupOption;
      this.selectedBackupName = backupList || '';
    }
  },

  methods: {
    shouldDisplayBackupDropdown() {
      return this.backupMachinesNamesList.length > 1 && this.backupMachinesNamesList[1] !== ''; // always containing 'None' option
    },
    updateSettingsObject(dataToUpdate: any) {
      if (this.setInputsDirty) {
        this.setInputsDirty(QUERY_RESULT_PATH.settings.common.backup.otherCoordinatorName, dataToUpdate.name);
        this.setInputsDirty(QUERY_RESULT_PATH.settings.common.backup.otherCoordinatorHost, dataToUpdate.ip);
        if (dataToUpdate.name !== '') this.setInputsDirty(QUERY_RESULT_PATH.settings.common.backup.otherCoordinatorPort, dataToUpdate.port);
      }
      this.$emit('update:modelValue', {
        OtherCoordDisplay: dataToUpdate.name,
        OtherCoordHost: dataToUpdate.ip,
        // if we choose 'None'(empty name) in backup options we don't need to send port
        OtherCoordPort: dataToUpdate.name !== '' ? dataToUpdate.port : this.modelValue.settings?.common?.backup?.otherCoordinatorPort,
      });
    },
    shouldUpdateSettingsObject(newData: any) {
      return this.modelValue.settings?.common?.backup?.otherCoordinatorName !== newData.name && this.modelValue.settings?.common?.backup?.otherCoordinatorHost !== newData.ip;
    },
    getBackupConfigData(selectedValue: any) {
      if (selectedValue === this.noConfiguredBackupOption) {
        return this.getBackupObjectDefault();
      }
      return (this.backupMachines as any)[selectedValue];
    },
    getBackupObjectDefault() {
      return {
        name: '',
        port: -1,
        ip: '',
        buildNum: '' as string | number,
      };
    },
    isIncompatibleBackupSelected(selectedBackup: any) {
      const coordinatorVersion = this.GQL_SettingsCoordinatorVersion.result.value?.ibManagerEntities?.coordinatorInfo?.buildVersion;
      return selectedBackup.name !== '' && selectedBackup.buildNum !== coordinatorVersion;
    },
    handleSelection(newValue: any) {
      const dataToUpdate = this.getBackupConfigData(newValue);
      this.shouldDisplayIncompatibleNote = this.isIncompatibleBackupSelected(dataToUpdate);
      if (dataToUpdate && this.shouldUpdateSettingsObject(dataToUpdate)) {
        this.selectedBackupName = newValue;
        this.updateSettingsObject(dataToUpdate);
      }
    },
  },
});
